import React, { useState, useCallback, useMemo } from 'react';

/**
 * Common peptide reference data for the dose calculator.
 * Includes typical dosing ranges and reconstitution defaults.
 */
const PEPTIDE_DATABASE = {
  bpc_157: {
    name: 'BPC-157',
    typical_dose_mcg: { min: 250, max: 500, default: 250 },
    frequency: '1-2x daily',
    typical_vial_mg: 5,
    route: 'SubQ or IM',
    timing: 'Empty stomach or near injury site',
    cycle: '4-6 weeks on, 2 weeks off',
    notes: 'Stable in stomach acid — can also be taken orally for gut healing'
  },
  tb_500: {
    name: 'TB-500',
    typical_dose_mcg: { min: 2000, max: 5000, default: 2500 },
    frequency: '2x weekly (loading), 1x weekly (maintenance)',
    typical_vial_mg: 5,
    route: 'SubQ',
    timing: 'Any time of day',
    cycle: '6-8 weeks loading, then maintenance',
    notes: 'Systemic healing peptide — no need to inject near injury'
  },
  bpc_tb_combo: {
    name: 'BPC-157 / TB-500 Combo',
    typical_dose_mcg: { min: 250, max: 500, default: 300 },
    frequency: '1x daily (BPC) + 2x weekly (TB)',
    typical_vial_mg: 10,
    route: 'SubQ',
    timing: 'BPC: AM empty stomach, TB: separate injection',
    cycle: '6-8 weeks',
    notes: 'Synergistic combination for tissue repair'
  },
  cjc_ipamorelin: {
    name: 'CJC-1295 / Ipamorelin',
    typical_dose_mcg: { min: 100, max: 300, default: 200 },
    frequency: '1-2x daily',
    typical_vial_mg: 5,
    route: 'SubQ',
    timing: 'Empty stomach — before bed or morning fasted (no food 1hr before/after)',
    cycle: '8-12 weeks on, 4 weeks off',
    notes: 'GH secretagogue. Avoid taking with food/sugar — blunts GH release'
  },
  cjc_1295_dac: {
    name: 'CJC-1295 DAC',
    typical_dose_mcg: { min: 1000, max: 2000, default: 2000 },
    frequency: '1x weekly',
    typical_vial_mg: 2,
    route: 'SubQ',
    timing: 'Any time — long half-life (8+ days)',
    cycle: '8-12 weeks on, 4 weeks off',
    notes: 'Long-acting GH releasing hormone. Steady GH elevation vs pulsatile'
  },
  tesamorelin: {
    name: 'Tesamorelin',
    typical_dose_mcg: { min: 1000, max: 2000, default: 2000 },
    frequency: '1x daily',
    typical_vial_mg: 2,
    route: 'SubQ (abdomen)',
    timing: 'Morning, empty stomach',
    cycle: '12+ weeks for fat reduction',
    notes: 'FDA-approved for visceral fat reduction. Best results after 12+ weeks'
  },
  sermorelin: {
    name: 'Sermorelin',
    typical_dose_mcg: { min: 200, max: 500, default: 300 },
    frequency: '1x daily (before bed)',
    typical_vial_mg: 3,
    route: 'SubQ',
    timing: 'Before bed, empty stomach',
    cycle: '3-6 months',
    notes: 'Gentle GH secretagogue. Good for beginners'
  },
  mk_677: {
    name: 'MK-677 (Ibutamoren)',
    typical_dose_mcg: { min: 10000, max: 25000, default: 15000 },
    frequency: '1x daily (oral)',
    typical_vial_mg: null,
    route: 'Oral',
    timing: 'Before bed (can increase appetite)',
    cycle: '8-12 weeks, some run continuously',
    notes: 'Oral GH secretagogue. May increase blood sugar and appetite. Not a peptide technically (SARM-adjacent)'
  },
  aod_9604: {
    name: 'AOD-9604',
    typical_dose_mcg: { min: 250, max: 500, default: 300 },
    frequency: '1x daily',
    typical_vial_mg: 5,
    route: 'SubQ (abdominal fat)',
    timing: 'Morning, empty stomach',
    cycle: '12 weeks',
    notes: 'Fat-burning fragment of HGH. No effect on blood sugar or growth'
  },
  pt_141: {
    name: 'PT-141 (Bremelanotide)',
    typical_dose_mcg: { min: 500, max: 2000, default: 1000 },
    frequency: 'As needed (45 min before)',
    typical_vial_mg: 10,
    route: 'SubQ',
    timing: '45 minutes before anticipated need',
    cycle: 'As needed, max 8 doses/month',
    notes: 'Works on melanocortin receptors. May cause nausea and flushing initially'
  },
  selank: {
    name: 'Selank',
    typical_dose_mcg: { min: 250, max: 750, default: 400 },
    frequency: '1-2x daily',
    typical_vial_mg: 5,
    route: 'Nasal spray or SubQ',
    timing: 'Morning and/or afternoon',
    cycle: '2-4 weeks on, 1-2 weeks off',
    notes: 'Anxiolytic peptide. Can improve focus and reduce anxiety without sedation'
  },
  semax: {
    name: 'Semax',
    typical_dose_mcg: { min: 200, max: 600, default: 400 },
    frequency: '1-2x daily',
    typical_vial_mg: 3,
    route: 'Nasal spray',
    timing: 'Morning for cognitive enhancement',
    cycle: '2-4 weeks on, 1-2 weeks off',
    notes: 'Nootropic peptide. Improves BDNF. Works well stacked with Selank'
  },
  epithalon: {
    name: 'Epithalon (Epitalon)',
    typical_dose_mcg: { min: 5000, max: 10000, default: 5000 },
    frequency: '1x daily for 10-20 day cycles',
    typical_vial_mg: 10,
    route: 'SubQ or IV',
    timing: 'Evening (supports melatonin production)',
    cycle: '10-20 days, 2x per year',
    notes: 'Telomerase activator. Short intense cycles, not continuous use'
  },
  ghk_cu: {
    name: 'GHK-Cu',
    typical_dose_mcg: { min: 200, max: 600, default: 200 },
    frequency: '1x daily',
    typical_vial_mg: 5,
    route: 'SubQ or topical',
    timing: 'Any time',
    cycle: '4-8 weeks',
    notes: 'Copper peptide for skin, hair, and wound healing. Also available as cream'
  }
};

/**
 * Peptide interaction rules for stacking safety.
 * Each rule defines a pair of peptides and the type of interaction.
 * 'conflict' = avoid combining, 'caution' = timing separation needed,
 * 'synergy' = beneficial combination, 'redundant' = overlapping mechanisms
 */
const INTERACTION_RULES = [
  // GH Secretagogue redundancy
  { a: 'cjc_ipamorelin', b: 'cjc_1295_dac', type: 'redundant', severity: 'high',
    message: 'Both are CJC-1295 variants. DAC version has a long half-life — combining adds little benefit and may desensitize GH receptors.' },
  { a: 'cjc_ipamorelin', b: 'sermorelin', type: 'redundant', severity: 'medium',
    message: 'Both are GH secretagogues targeting the same pathway. Pick one — stacking provides diminishing returns.' },
  { a: 'cjc_ipamorelin', b: 'mk_677', type: 'caution', severity: 'medium',
    message: 'Both elevate GH. MK-677 is continuous while CJC/Ipa is pulsatile. Together may cause excessive GH elevation. Monitor for side effects (water retention, numbness).' },
  { a: 'sermorelin', b: 'mk_677', type: 'caution', severity: 'medium',
    message: 'Both stimulate GH release. MK-677 keeps GH elevated 24/7 — adding Sermorelin may not add benefit. Consider one or the other.' },
  { a: 'cjc_1295_dac', b: 'sermorelin', type: 'redundant', severity: 'high',
    message: 'CJC-1295 DAC provides sustained GH release over 8+ days. Adding Sermorelin (short-acting) is redundant.' },
  { a: 'cjc_1295_dac', b: 'mk_677', type: 'caution', severity: 'medium',
    message: 'Both provide sustained GH elevation. Stacking increases risk of side effects (edema, insulin resistance). Use lower doses if combining.' },
  { a: 'tesamorelin', b: 'cjc_ipamorelin', type: 'redundant', severity: 'medium',
    message: 'Both are GHRH analogs. Tesamorelin is FDA-approved and more potent — adding CJC/Ipa offers minimal extra benefit.' },
  { a: 'tesamorelin', b: 'sermorelin', type: 'redundant', severity: 'medium',
    message: 'Both target the GHRH receptor. Tesamorelin is stronger — Sermorelin is unnecessary alongside it.' },
  { a: 'tesamorelin', b: 'mk_677', type: 'caution', severity: 'medium',
    message: 'Both raise GH. Combining may increase insulin resistance. Monitor blood sugar closely.' },

  // Healing peptide synergies
  { a: 'bpc_157', b: 'tb_500', type: 'synergy', severity: 'positive',
    message: 'Excellent stack for tissue repair. BPC-157 works locally at the injury site, TB-500 works systemically. Inject BPC near injury, TB anywhere SubQ.' },
  { a: 'bpc_157', b: 'ghk_cu', type: 'synergy', severity: 'positive',
    message: 'Complementary healing: BPC-157 for deep tissue and gut, GHK-Cu for skin, collagen, and surface wounds.' },
  { a: 'tb_500', b: 'ghk_cu', type: 'synergy', severity: 'positive',
    message: 'Both support tissue repair through different mechanisms. TB-500 for systemic healing, GHK-Cu for skin and hair.' },

  // Cognitive synergies
  { a: 'selank', b: 'semax', type: 'synergy', severity: 'positive',
    message: 'Classic nootropic stack. Selank reduces anxiety (GABAergic), Semax boosts focus and BDNF. Take both in the morning via nasal spray.' },

  // Timing conflicts
  { a: 'cjc_ipamorelin', b: 'aod_9604', type: 'caution', severity: 'low',
    message: 'Both should be taken fasted. Separate injections by 30+ minutes for optimal absorption. Take AOD in AM, CJC/Ipa before bed.' },
  { a: 'mk_677', b: 'aod_9604', type: 'caution', severity: 'medium',
    message: 'MK-677 increases appetite and can raise blood sugar — may counteract AOD-9604\'s fat-burning effects. Consider choosing one based on your primary goal.' },

  // Special interactions
  { a: 'pt_141', b: 'mk_677', type: 'caution', severity: 'low',
    message: 'Both can cause nausea. If stacking, use PT-141 on separate days from MK-677 to reduce GI side effects.' },
  { a: 'epithalon', b: 'bpc_157', type: 'synergy', severity: 'positive',
    message: 'Complementary anti-aging stack. Epithalon for telomere support (short cycles), BPC-157 for ongoing tissue maintenance.' },
];

/**
 * Check a user's peptide stack for interactions.
 * Returns array of { type, severity, message, peptideA, peptideB }
 */
function getStackInteractions(userPeptideKeys) {
  if (!userPeptideKeys || userPeptideKeys.length < 2) return [];
  const validKeys = userPeptideKeys.filter(k => k !== 'none' && k !== 'other_peptide' && PEPTIDE_DATABASE[k]);
  const results = [];
  for (const rule of INTERACTION_RULES) {
    if (validKeys.includes(rule.a) && validKeys.includes(rule.b)) {
      results.push({
        type: rule.type,
        severity: rule.severity,
        message: rule.message,
        peptideA: PEPTIDE_DATABASE[rule.a]?.name || rule.a,
        peptideB: PEPTIDE_DATABASE[rule.b]?.name || rule.b,
      });
    }
  }
  // Sort: conflicts/redundant first, caution next, synergy last
  const order = { redundant: 0, conflict: 1, caution: 2, synergy: 3 };
  results.sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9));
  return results;
}

/**
 * Peptide Dose Calculator Component
 * Handles reconstitution math and protocol building.
 */
const PeptideDoseCalculator = ({ userId, onAskCoach, userPeptides }) => {
  const [selectedPeptide, setSelectedPeptide] = useState('');
  const [vialMg, setVialMg] = useState('');
  const [waterMl, setWaterMl] = useState('');
  const [targetDoseMcg, setTargetDoseMcg] = useState('');
  const [result, setResult] = useState(null);
  const [protocolView, setProtocolView] = useState(false);

  // Compute stack interactions from user's peptides
  const interactions = useMemo(() => getStackInteractions(userPeptides), [userPeptides]);

  // When peptide is selected, pre-fill defaults
  const handlePeptideSelect = useCallback((peptideKey) => {
    setSelectedPeptide(peptideKey);
    const p = PEPTIDE_DATABASE[peptideKey];
    if (p) {
      if (p.typical_vial_mg) setVialMg(String(p.typical_vial_mg));
      setWaterMl('2'); // Standard 2ml BAC water
      setTargetDoseMcg(String(p.typical_dose_mcg.default));
      setResult(null);
    }
  }, []);

  // Calculate dose
  const calculate = useCallback(() => {
    const vial = parseFloat(vialMg);
    const water = parseFloat(waterMl);
    const dose = parseFloat(targetDoseMcg);
    
    if (!vial || !water || !dose) return;
    
    const totalMcg = vial * 1000; // Convert mg to mcg
    const mcgPerMl = totalMcg / water;
    const mlPerDose = dose / mcgPerMl;
    const unitsPerDose = mlPerDose * 100; // 1ml = 100 units on insulin syringe
    const dosesPerVial = Math.floor(totalMcg / dose);
    
    setResult({
      mcgPerMl: Math.round(mcgPerMl),
      mlPerDose: mlPerDose.toFixed(3),
      unitsPerDose: unitsPerDose.toFixed(1),
      dosesPerVial,
      totalMcg
    });
  }, [vialMg, waterMl, targetDoseMcg]);

  const peptideInfo = PEPTIDE_DATABASE[selectedPeptide];

  return (
    <div data-testid="peptide-dose-calculator" style={{
      background: 'rgba(255,255,255,0.04)',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '16px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 data-testid="peptide-calc-title" style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#e0e0e0', letterSpacing: '0.5px' }}>
          PEPTIDE DOSE CALCULATOR
        </h3>
        <button
          data-testid="peptide-toggle-protocol"
          onClick={() => setProtocolView(!protocolView)}
          style={{
            background: protocolView ? 'rgba(102,126,234,0.2)' : 'rgba(255,255,255,0.08)',
            border: 'none',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '600',
            color: protocolView ? '#667eea' : '#999',
            cursor: 'pointer'
          }}
        >
          {protocolView ? 'Calculator' : 'Protocol Info'}
        </button>
      </div>

      {/* Peptide Selector */}
      <select
        data-testid="peptide-selector"
        value={selectedPeptide}
        onChange={(e) => handlePeptideSelect(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '10px',
          color: '#e0e0e0',
          fontSize: '14px',
          marginBottom: '12px',
          outline: 'none'
        }}
      >
        <option value="">Select a peptide...</option>
        {Object.entries(PEPTIDE_DATABASE).map(([key, p]) => (
          <option key={key} value={key}>{p.name}</option>
        ))}
      </select>

      {/* Stack Interaction Warnings */}
      {interactions.length > 0 && (
        <div data-testid="peptide-stack-interactions" style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#ccc', letterSpacing: '0.5px', marginBottom: '8px', textTransform: 'uppercase' }}>
            Stack Interactions ({interactions.length})
          </div>
          {interactions.map((item, i) => {
            const isWarning = item.type === 'redundant' || item.type === 'conflict';
            const isCaution = item.type === 'caution';
            const isSynergy = item.type === 'synergy';
            const bgColor = isWarning ? 'rgba(239, 68, 68, 0.08)' : isCaution ? 'rgba(251, 191, 36, 0.08)' : 'rgba(56, 239, 125, 0.08)';
            const borderColor = isWarning ? 'rgba(239, 68, 68, 0.2)' : isCaution ? 'rgba(251, 191, 36, 0.2)' : 'rgba(56, 239, 125, 0.2)';
            const labelColor = isWarning ? '#ef4444' : isCaution ? '#fbbf24' : '#38ef7d';
            const label = isWarning ? 'AVOID' : isCaution ? 'CAUTION' : 'SYNERGY';
            const icon = isWarning ? '⚠️' : isCaution ? '⏱️' : '✅';

            return (
              <div key={i} data-testid={`peptide-interaction-${i}`} style={{
                background: bgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '10px',
                padding: '10px 12px',
                marginBottom: i < interactions.length - 1 ? '8px' : '0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px' }}>{icon}</span>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: labelColor, letterSpacing: '0.5px' }}>{label}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>
                    {item.peptideA} + {item.peptideB}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '11px', color: '#bbb', lineHeight: '1.5' }}>
                  {item.message}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {selectedPeptide && !protocolView && (
        <>
          {/* Calculator Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
            <div>
              <label style={{ fontSize: '10px', color: '#888', display: 'block', marginBottom: '4px' }}>Vial Size (mg)</label>
              <input
                data-testid="peptide-vial-mg"
                type="number"
                value={vialMg}
                onChange={(e) => { setVialMg(e.target.value); setResult(null); }}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', color: '#888', display: 'block', marginBottom: '4px' }}>BAC Water (mL)</label>
              <input
                data-testid="peptide-water-ml"
                type="number"
                value={waterMl}
                onChange={(e) => { setWaterMl(e.target.value); setResult(null); }}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', color: '#888', display: 'block', marginBottom: '4px' }}>Dose (mcg)</label>
              <input
                data-testid="peptide-dose-mcg"
                type="number"
                value={targetDoseMcg}
                onChange={(e) => { setTargetDoseMcg(e.target.value); setResult(null); }}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Dose Range Hint */}
          {peptideInfo && (
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px' }}>
              Typical dose: {peptideInfo.typical_dose_mcg.min}-{peptideInfo.typical_dose_mcg.max} mcg ({peptideInfo.frequency})
            </div>
          )}

          {/* Calculate Button */}
          <button
            data-testid="peptide-calculate-btn"
            onClick={calculate}
            disabled={!vialMg || !waterMl || !targetDoseMcg}
            style={{
              width: '100%',
              padding: '10px',
              background: (!vialMg || !waterMl || !targetDoseMcg) ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (!vialMg || !waterMl || !targetDoseMcg) ? 'default' : 'pointer',
              opacity: (!vialMg || !waterMl || !targetDoseMcg) ? 0.4 : 1,
              marginBottom: '14px'
            }}
          >
            Calculate Dose
          </button>

          {/* Results */}
          {result && (
            <div data-testid="peptide-calc-results" style={{
              background: 'rgba(102,126,234,0.08)',
              borderRadius: '12px',
              padding: '14px',
              border: '1px solid rgba(102,126,234,0.15)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#667eea' }}>{result.unitsPerDose}</div>
                  <div style={{ fontSize: '10px', color: '#999' }}>units per dose</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#38ef7d' }}>{result.dosesPerVial}</div>
                  <div style={{ fontSize: '10px', color: '#999' }}>doses per vial</div>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#aaa', textAlign: 'center' }}>
                {result.mcgPerMl} mcg/mL concentration &middot; {result.mlPerDose} mL per dose
              </div>
            </div>
          )}
        </>
      )}

      {/* Protocol View */}
      {selectedPeptide && protocolView && peptideInfo && (
        <div data-testid="peptide-protocol-info" style={{ fontSize: '13px', color: '#ccc' }}>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ color: '#667eea', fontWeight: '600' }}>Route:</span> {peptideInfo.route}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ color: '#667eea', fontWeight: '600' }}>Timing:</span> {peptideInfo.timing}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ color: '#667eea', fontWeight: '600' }}>Frequency:</span> {peptideInfo.frequency}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ color: '#667eea', fontWeight: '600' }}>Cycle:</span> {peptideInfo.cycle}
          </div>
          <div style={{ marginBottom: '14px' }}>
            <span style={{ color: '#667eea', fontWeight: '600' }}>Notes:</span> {peptideInfo.notes}
          </div>
          
          {/* Ask Coach about this peptide */}
          {onAskCoach && (
            <button
              data-testid="peptide-ask-coach-btn"
              onClick={() => onAskCoach(`Help me optimize my ${peptideInfo.name} protocol — timing, dosing, and any interactions with my current medications and supplements`)}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(102,126,234,0.15)',
                border: '1px solid rgba(102,126,234,0.25)',
                borderRadius: '10px',
                color: '#667eea',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Ask Smart Coach about {peptideInfo.name}
            </button>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <p style={{ fontSize: '9px', color: '#666', margin: '12px 0 0', textAlign: 'center', lineHeight: '1.4' }}>
        For informational purposes only. Not medical advice. Consult your healthcare provider before starting any peptide protocol.
      </p>
    </div>
  );
};

export default PeptideDoseCalculator;
